import type { Problem } from '@harbor/shared';

/**
 * API client.
 *
 * ## Where the access token lives
 *
 * In a module-scoped variable. Not `localStorage`, not `sessionStorage`, not a
 * readable cookie. Anything reachable from `document` is reachable from any
 * script on the origin, which means one compromised dependency, one injected
 * tag, or one careless `dangerouslySetInnerHTML` hands over a working session.
 *
 * The cost of keeping it in memory is that a page refresh loses it — which is
 * exactly what the refresh cookie is for. On load the client calls
 * `POST /api/auth/refresh`, gets a new access token, and carries on. The user
 * sees a brief loading state, not a sign-out.
 *
 * ## Refreshing exactly once
 *
 * When a page fires four requests and the access token has expired, all four get
 * a 401 at the same moment. Refreshing in each of them would rotate the refresh
 * token four times; the second rotation would present an already-used token, the
 * server would (correctly) treat that as a leaked token and revoke the whole
 * session family. A single-flight promise is therefore not an optimisation here,
 * it is a correctness requirement.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
    readonly raw: unknown,
  ) {
    super(problem.detail || problem.title);
    this.name = 'ApiError';
  }

  /** True for 5xx and network failures — worth offering a retry for. */
  get isTransient(): boolean {
    return this.status >= 500 || this.status === 0;
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Notified when a refresh attempt fails for good, so the UI can sign the user out. */
type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

async function refreshAccessToken(): Promise<boolean> {
  // Single-flight: concurrent callers await the same promise.
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) return false;

      const body = (await response.json()) as { accessToken: string };
      accessToken = body.accessToken;
      return true;
    } catch {
      // A network failure is not proof that the session is dead — the caller
      // decides what to do. Reporting `false` here means "could not refresh",
      // and only a definite 4xx triggers the sign-out below.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Set for endpoints that require it. The value must be unique per logical operation. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Internal: prevents an infinite refresh loop. */
  _retried?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, idempotencyKey, signal, _retried = false } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      // Sends the refresh cookie on `/api/auth/*` and, in the same-origin
      // deployment, on everything else too.
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(
      0,
      {
        type: 'https://harbor.dev/errors/network',
        title: 'Network Error',
        status: 0,
        detail: 'Could not reach the server. Check your connection and try again.',
      },
      null,
    );
  }

  if (response.status === 401 && !_retried && !path.startsWith('/api/auth/')) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return apiRequest<T>(path, { ...options, _retried: true });
    }
    // The refresh endpoint itself refused: the session is genuinely over.
    for (const listener of sessionExpiredListeners) listener();
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('Content-Type') ?? '';
  const payload: unknown = contentType.includes('json') ? await response.json() : await response.text();

  if (!response.ok) {
    throw new ApiError(response.status, toProblem(payload, response.status), payload);
  }

  return payload as T;
}

/**
 * Every non-2xx response from this API is an RFC 9457 problem document, so this
 * is the only error-shape translation the client needs. The fallback exists for
 * the case where something in front of the Worker answers instead — a Cloudflare
 * 502 during a deploy, for instance — and returns HTML.
 */
function toProblem(payload: unknown, status: number): Problem {
  if (typeof payload === 'object' && payload !== null && 'title' in payload) {
    return payload as Problem;
  }
  return {
    type: 'https://harbor.dev/errors/unexpected',
    title: 'Unexpected Response',
    status,
    detail:
      typeof payload === 'string' && payload.length > 0 && payload.length < 300
        ? payload
        : 'The server returned a response the client could not interpret.',
  };
}

/** Stable, unique-per-call idempotency key. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/* -------------------------------------------------------------------------- */
/*  Typed endpoint helpers                                                    */
/* -------------------------------------------------------------------------- */

export interface ProductQueryParams {
  q?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  sort?: string;
  cursor?: string;
  limit?: number;
}

export function toQueryString(params: ProductQueryParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}
